import type { TrendPoint } from '../../../hooks/pipelineTracker/useTrendsReport';

/**
 * The Trends tab's range and its period-over-period comparison (founder, 5 Oct 2026: "should
 * trends have a date selector or something for comparison to last quarter, month?").
 *
 * The book is STOCK, photographed once per upload, so a range is read at its snapshots:
 *   - `start` is the first snapshot inside the range, `end` the last. "Since <start>" is end minus
 *     start.
 *   - The PREVIOUS range is the same length immediately before. Its change is start minus
 *     `prevStart`, the first snapshot inside it, so the two changes are contiguous and add up to
 *     the change across both ranges.
 *   - The previous range exists only when the history reaches back to it: `prevStart` must sit
 *     within a week of the previous range's first day (weekly uploads), or the comparison would
 *     set a full range against a few weeks of it. Otherwise the reason is returned and the card
 *     says so ("No earlier 13 weeks"). Nothing is interpolated.
 *
 * Pure: the same points and choice always give the same range.
 */

export type RangeKey = '4w' | '13w' | '26w' | 'all' | 'custom';

export const RANGE_PRESETS: ReadonlyArray<{ key: RangeKey; label: string }> = [
  { key: '4w', label: '4W' },
  { key: '13w', label: '13W' },
  { key: '26w', label: '26W' },
  { key: 'all', label: 'ALL' },
];

/** A quarter: the range most practices compare. */
export const DEFAULT_RANGE: RangeKey = '13w';

const WEEKS: Record<'4w' | '13w' | '26w', number> = { '4w': 4, '13w': 13, '26w': 26 };

/** Uploads are weekly, so a snapshot within a week of a range's first day covers it. */
const COVER_DAYS = 7;

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export interface TrendRange {
  key: RangeKey;
  /** Both inclusive, ISO dates. */
  from: string;
  to: string;
  /** Length in days, or null for ALL, which has no previous range by definition. */
  days: number | null;
  /** '13 weeks', '30 days', 'history': for "vs previous 13 weeks" and "No earlier 13 weeks". */
  label: string;
}

/** The chosen range over `points` (oldest first, at least one). */
export function resolveRange(
  key: RangeKey,
  points: readonly TrendPoint[],
  custom?: { from: string; to: string },
): TrendRange {
  const first = points[0].asAtDate;
  const last = points[points.length - 1].asAtDate;
  if (key === 'custom' && custom) {
    const days = daysBetween(custom.from, custom.to);
    return { key, from: custom.from, to: custom.to, days, label: `${days} days` };
  }
  if (key === '4w' || key === '13w' || key === '26w') {
    const weeks = WEEKS[key];
    return { key, from: addDays(last, -7 * weeks), to: last, days: 7 * weeks, label: `${weeks} weeks` };
  }
  // ALL, and CUSTOM before any dates are applied.
  return { key: key === 'custom' ? 'custom' : 'all', from: first, to: last, days: null, label: 'history' };
}

export const pointsInRange = (points: readonly TrendPoint[], range: TrendRange): TrendPoint[] =>
  points.filter((p) => p.asAtDate >= range.from && p.asAtDate <= range.to);

export type PreviousRange =
  | { status: 'ok'; prevStart: TrendPoint; prevFrom: string }
  | { status: 'unavailable'; reason: string };

export interface RangeReading {
  range: TrendRange;
  points: TrendPoint[];
  start: TrendPoint | null;
  end: TrendPoint | null;
  previous: PreviousRange;
}

export function readRange(all: readonly TrendPoint[], range: TrendRange): RangeReading {
  const points = pointsInRange(all, range);
  const start = points[0] ?? null;
  const end = points[points.length - 1] ?? null;
  return { range, points, start, end, previous: previousRange(all, range, start) };
}

function previousRange(all: readonly TrendPoint[], range: TrendRange, start: TrendPoint | null): PreviousRange {
  if (range.days === null) return { status: 'unavailable', reason: 'The whole history is shown' };
  const none: PreviousRange = { status: 'unavailable', reason: `No earlier ${range.label}` };
  if (!start) return none;
  const prevFrom = addDays(range.from, -range.days);
  const prevStart = all.find((p) => p.asAtDate >= prevFrom && p.asAtDate < start.asAtDate);
  if (!prevStart || prevStart.asAtDate > addDays(prevFrom, COVER_DAYS)) return none;
  return { status: 'ok', prevStart, prevFrom };
}

/** Change from `a` to `b`, and as a percentage of `a` when `a` is above zero. */
export function change(a: number, b: number): { diff: number; pct: number | null } {
  return { diff: b - a, pct: a > 0 ? ((b - a) / a) * 100 : null };
}

/** One stage's share of the deals in flight at a snapshot, as a percentage. */
export function stageShare(p: TrendPoint, label: string): number {
  const total = Object.values(p.stages).reduce((a, b) => a + b, 0);
  return total > 0 ? ((p.stages[label] ?? 0) / total) * 100 : 0;
}
